import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { PencilSimple } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { Trash } from "@phosphor-icons/react/dist/ssr/Trash";
interface Announcement {
  id: string;
  title: string;
  message: string;
  created: string;
  isActive: boolean;
  expirationDate?: string;
}

interface AdminAnnouncementCardProps {
  disabledEdit?: boolean;
  announcement: Announcement;
  onEdit: (announcement: Announcement) => void;
  onDelete: (announcementId: string, title: string) => void;
}

const AdminAnnouncementCard: React.FC<AdminAnnouncementCardProps> = ({
  announcement,
  onEdit,
  onDelete,
  disabledEdit = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const stripHtmlTags = (html: string) => {
    if (!html) return "";
    const withoutTags = html.replace(/<[^>]*>/g, "");
    // Create a temporary div to decode HTML entities
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = withoutTags;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const handleEdit = () => {
    onEdit(announcement);
  };

  const handleDelete = () => {
    onDelete(announcement.id, announcement.title);
  };

  const handleReadMoreToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const messageText = stripHtmlTags(announcement.message);
  const shouldShowReadMore = messageText.length > 150;

  const statusBadge = announcement.isActive ? (
    <View style={styles.publishedBadge}>
      <CustomText text="PUBLISHED" weight="bold" style={styles.publishedText} useTranslationText={false} />
    </View>
  ) : (
    <View style={styles.inactiveBadge}>
      <CustomText text="INACTIVE" weight="bold" style={styles.inactiveText} useTranslationText={false} />
    </View>
  );

  return (
    <View style={styles.container}>
      {/* Main Content */}
      <View style={styles.content}>
        <View style={styles.headerRow}>
          <CustomText
            text={announcement.title}
            size="m"
            weight="bold"
            style={styles.title}
            useTranslationText={false}
          />
          <View style={styles.rightSection}>
            <View style={styles.dateAndBadgeRow}>
              <CustomText
                text={formatDate(announcement.created)}
                size="xs"
                style={styles.date}
                useTranslationText={false}
              />
              {statusBadge}
            </View>
            {announcement.expirationDate && (
              <CustomText
                text={`Expires ${formatDate(announcement.expirationDate)}`}
                size="xs"
                style={styles.expirationDate}
                useTranslationText={false}
              />
            )}
          </View>
        </View>

        <CustomSpacing type="vertical" size="s" />

        <CustomText
          text={isExpanded || !shouldShowReadMore ? messageText : `${messageText.substring(0, 150)}...`}
          size="s"
          style={styles.message}
          useTranslationText={false}
        />

        {shouldShowReadMore && (
          <>
            <CustomSpacing type="vertical" size="s" />
            <TouchableOpacity onPress={handleReadMoreToggle} style={styles.readMoreButton}>
              <CustomText
                text={isExpanded ? "Read Less" : "Read More"}
                size="s"
                style={styles.readMoreText}
                useTranslationText={false}
              />
            </TouchableOpacity>
          </>
        )}

        <CustomSpacing type="vertical" size="m" />

        {/* Action Buttons */}
        <View style={styles.actionsRow}>
          {!disabledEdit && (
            <>
              <CustomButton
                text="Edit"
                styleType="transparent"
                size="s"
                leftIcon={<PencilSimple style={{ marginRight: -8 }} size={20} weight="bold" color="#3E68FE" />}
                onPress={handleEdit}
                style={styles.actionButton}
                textStyle={styles.actionButtonText}
                useTranslationText={false}
              />
              <CustomButton
                text="Delete"
                styleType="transparent"
                size="s"
                leftIcon={<Trash style={{ marginRight: -8 }} size={20} weight="bold" color="#3E68FE" />}
                onPress={handleDelete}
                style={styles.actionButton}
                textStyle={styles.actionButtonText}
                useTranslationText={false}
              />
            </>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderLeftWidth: 4,
    borderLeftColor: "#10B981",
    overflow: "hidden",
  },
  content: {
    padding: 20,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  title: {
    color: "#1E293B",
    flex: 1,
  },
  rightSection: {
    alignItems: "flex-end",
  },
  dateAndBadgeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  date: {
    color: "#64748B",
  },
  expirationDate: {
    color: "#EF4444",
    marginTop: 2,
  },
  publishedBadge: {
    backgroundColor: "#D1FAE5",
    paddingHorizontal: 8,
    borderRadius: 12,
  },
  publishedText: {
    color: "#065F46",
    fontSize: 11,
    letterSpacing: 0.5,
  },
  inactiveBadge: {
    backgroundColor: "#6B7280",
    paddingHorizontal: 8,
    borderRadius: 12,
  },
  inactiveText: {
    color: "#FFFFFF",
    fontSize: 11,
    letterSpacing: 0.5,
  },
  message: {
    color: "#374151",
    lineHeight: 21,
  },
  readMoreButton: {
    alignSelf: "flex-start",
  },
  readMoreText: {
    color: "#3E68FE",
    fontWeight: "600",
  },
  actionsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  actionButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    minWidth: 0,
    borderColor: "#05060F1A",
    borderWidth: 1,
    justifyContent: "center",
  },
  actionButtonText: {
    color: "#3E68FE",
    marginLeft: 4,
    fontWeight: "700",
    fontSize: 14,
  },
});

export default AdminAnnouncementCard;
